import moment from 'moment'; // Importing moment library for date manipulation
import i18next from "i18next";
import {FILES_ICON, getFileIcon} from '../Helpers/fileType'; // Importing file type icons mapping from a helper file
import pdf_image from '../assets/images/pdf.svg'; // Importing default PDF image icon
import Cookies from 'js-cookie';
import cryptoJs from 'crypto-js';
import securekey from '../config';
import {errorCodeTranslation, warnCodeTranslation} from './ErrorCode';
import {toast} from 'react-toastify'; // Importing the js-cookie library for managing cookies
import 'moment/locale/es';
import 'moment/locale/fr';
import i18n, {t} from 'i18next';
import {
    CompletePersonChip,
    EmailSubsectionChip, IconChipCircleTools,
    IconColorPalette,
    IconFileEmpty, ModelIcon, NumericFieldChip,
    PhoneSubsectionChip,
    VehicleChip
} from '../components/buttons/IconButtons';
import {FIELD_TYPE} from "../pages/Dashboard/Visitor/Type/Model/Model";
import {createFilterOptions} from '@mui/material/Autocomplete';
import React, {useEffect, useState} from "react";
import CustomTooltip from "../components/CustomTooltip";
import {getDeviceTypeById} from "../hooks/deviceTypeEnum";
import {Trans} from "react-i18next";
import {TABLES} from "./Tables";
import {BACK_TO_UI_LANG, UI_TO_BACK_LANG} from "../pages/Home/Model/HomeModel";

// Fetching the language code from cookies, defaults to 'en' (English) if not set
const lCode = Cookies.get('i18next') || 'en';
let currentFetch = null;

// Setting a debounce delay constant to be used for functions that require delay
export const DEBOUNCE_DELAY = 1000; // 1 second debounce delay

// Regular expression for validating phone numbers with an optional international code
export const PHONE_REGEX = /^\+?\d{1,4}\d{10}$/;

// Regular expression for validating email addresses
export const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,6}$/i;

export const IP_REGEX = /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;

export const DECIMAL_REGEX = /^(0(\.\d{1,2})?|[1-9]\d*(\.\d{1,2})?)$/;

export const DECIMAL_INPUT_PROPS = {
    pattern: DECIMAL_REGEX.source
};

export const DEFAULT_LATITUDE = 20.5888;
export const DEFAULT_LONGITUDE = -100.3899;
export const MIN_YEAR = 1900;
export const MAX_YEAR = 2099;
export const MS_IN_DAY = 24 * 60 * 60 * 1000;

export const isNumericString = (v) => typeof v === "string" && /^-?\d+$/.test(v.trim());

export const DAYS_MODEL = [
    { value: 1, key: 'monday', label: 'm_monday' },
    { value: 2, key: 'tuesday', label: 't_tuesday' },
    { value: 3, key: 'wednesday', label: 'w_wednesday' },
    { value: 4, key: 'thursday', label: 't_thursday' },
    { value: 5, key: 'friday', label: 'f_friday' },
    { value: 6, key: 'saturday', label: 's_saturday' },
    { value: 0, key: 'sunday', label: 's_sunday' },
];

// Mapping weekdays (starting on Monday)
export const WEEKDAYS_MODEL = [
    'monday',    // 0
    'tuesday',   // 1
    'wednesday', // 2
    'thursday',  // 3
    'friday',    // 4
    'saturday',  // 5
    'sunday'     // 6
];
// Short weekday labels for UI display
export const WEEKDAYS_SHORT_MODEL = {
    en: ["M", "T", "W", "T", "F", "S", "S"],     // Monday first
    sp: ["L", "M", "X", "J", "V", "S", "D"],
    fr: ["L", "M", "M", "J", "V", "S", "D"],
};

export const DATE_TIME_FORMAT_MODEL = {
    en: {
        slash: 'MM/DD/YYYY',
        dash: 'MM-DD-YYYY',
        shortMonth: 'MMM DD, YYYY',
        dateTime24Hour: "YYYY-MM-DD HH:mm:ss",
        dateTime12Hour: "MMM DD, hh:mm a",
        shortMonthWithTime12: "MMM DD, hh:mm A",
        dayWithShortMonth: "dddd, MMM DD",
        time12Uppercase: "hh:mm A",
        monthWithDay: "MMMM, DD"
    },
    es: {
        slash: 'DD/MM/YYYY',
        dash: 'DD-MM-YYYY',
        shortMonth: 'DD MMM YYYY',
        dateTime24Hour: "DD-MM-YYYY HH:mm:ss",
        dateTime12Hour: "DD MMM YYYY hh:mm a",
        shortMonthWithTime12: "MMM DD, hh:mm A",
        dayWithShortMonth: "dddd, MMM DD",
        time12Uppercase: "hh:mm A",
        monthWithDay: "DD [de] MMMM"
    },
    fr: {
        slash: 'DD/MM/YYYY',
        dash: 'DD-MM-YYYY',
        shortMonth: 'DD MMM YYYY',
        dateTime24Hour: "DD-MM-YYYY HH:mm:ss",
        dateTime12Hour: "DD MMM YYYY hh:mm a",
        shortMonthWithTime12: "MMM DD, hh:mm A",
        dayWithShortMonth: "dddd, MMM DD",
        time12Uppercase: "hh:mm A",
        monthWithDay: "DD MMMM"
    }
};

export const DATE_TIME_FORMAT_NAME_MODEL = {
    slash:"slash",
    shortMonth: "shortMonth",
    HHmm: "HH:mm",
    HHmmss: "HH:mm:ss",
    hhmmss12: "hh:mm:ss A",
    MMMM_YYYY: "MMMM yyyy",
    dateTime24Hour: "dateTime24Hour",
    dateTime12Hour: "dateTime12Hour",
    shortMonthWithTime12: "shortMonthWithTime12",
    dayWithShortMonth: "dayWithShortMonth",
    time12Uppercase: "time12Uppercase",
    dateTimeWithSeconds: "DD/MM/YYYY HH:mm:ss",
    dateTimeWithSeconds12: "DD/MM/YYYY hh:mm:ss A",
    mmss: "mm:ss",
    hours: "hours",
    minutes: "minutes",
    seconds: "seconds",
    ms: "ms",
    isoDate: "YYYY-MM-DD"
};

// Function to validate and format a date into a "DD/MM/YYYY" format using moment.js
export const validDateEx = (v) => {
    return v ? moment(v).format('DD/MM/YYYY') : null; // If value is provided, format the date, else return null
};

/**
 * Checks whether the provided value is a valid date.
 *
 * Accepts both `Date` objects and numbers (timestamps).
 *
 * @param {Date|number} date - The value to validate, can be a Date object or a number.
 * @returns {boolean} `true` if the value is a valid Date or a valid timestamp, otherwise `false`.
 */
export const hasValidDate = (date) => {
    return (date instanceof Date && !isNaN(date)) ||
        (typeof date === 'number' && !isNaN(date));
};

/**
 * formatTimeUnit
 * Converts a total number of minutes into a human-readable string format.
 * The function formats the time as follows:
 * - If total minutes are 0, returns "0m".
 * - If less than 60 minutes, returns only the minutes (e.g., "45m").
 * - If 60 minutes or more, returns hours and remaining minutes (e.g., "2h 15m").
 * - If the number of minutes is an exact multiple of 60, returns only the hours (e.g., "3h").
 *
 * @param {number|string} totalMinutes - The total number of minutes to convert. Can be a number or a numeric string.
 * @returns {string} A formatted string representing hours and minutes.
 */
export const formatTimeUnit = (totalMinutes) => {
    const minutesInt = parseInt(totalMinutes, 10);
    if (isNaN(minutesInt) || minutesInt < 0) return '';

    const hours = Math.floor(minutesInt / 60);
    const minutes = minutesInt % 60;

    if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
        return `${hours}h`;
    } else {
        return `${minutes}m`;
    }
};

/**
 * Formats a date into a localized string according to the current language and provided options.
 * - Returns null if `date` is not provided.
 * - Detects the current language using `i18n.language` and adjusts to 'en', 'es', or 'fr'; any other language defaults to 'en'.
 * - Allows using a custom format via `customFormat`; if not provided, the default slash format is used.
 * - If `datetime` is true, includes the time in 24-hour format at the end of the string.
 *
 * @param date Date | string - The date or Date object to format.
 * @param datetime boolean - Indicates whether to include the time (default: true).
 * @param customFormat string - Custom date format or key from DATE_TIME_FORMAT_MODEL (optional).
 * @returns string | null - Formatted date string according to the options, or null if no date is provided.
 */
export const getDateFormat = (date, datetime = true, customFormat = '') => {
    if (date === null || date === undefined) return null; // If no date is provided, return null

    let lCode = i18n.language || 'en';
    if (lCode.startsWith('sp')) lCode = 'es';
    if (!['en', 'es', 'fr'].includes(lCode)) lCode = 'en';

    const format = DATE_TIME_FORMAT_MODEL[lCode][customFormat] || customFormat || DATE_TIME_FORMAT_MODEL[lCode].slash;

    if (datetime == null && customFormat) {
        return moment(date).locale(lCode).format(format);
    }
    return (datetime) ?  moment(date).locale(lCode).format(format+ " HH:mm") : moment(date).locale(lCode).format(format);
};


/**
 * Formats a Date object or datetime string into a time string based on the current language.
 * - Returns null if `datetime` is not provided.
 * - Dynamically detects the current language using `i18n.language`.
 * - Uses 12-hour format (with AM/PM) for languages 'sp', 'es', and 'en'; otherwise, uses 24-hour format.
 *
 * @param datetime Date | string - The date or Date object to format.
 * @returns string | null - The formatted time as "HH:mm:ss" or "hh:mm:ss A" depending on the language, or null if no date is provided.
 */
export const getTimeFormat = (datetime) => {
    if (!datetime) return null;
    const lang = i18n.language?.split('-')[0] || 'en';
    const use12h = ['sp', 'es', 'en'].includes(lang);
    return  moment(datetime).format(use12h ? DATE_TIME_FORMAT_NAME_MODEL.hhmmss12 : DATE_TIME_FORMAT_NAME_MODEL.HHmmss);
};

/**
 * Converts a valid time string into a formatted "HH:MM:SS" string.
 * - If the input is already a string in the format "HH:MM:SS", it returns it as is.
 * - If the timestamp is null or invalid, it returns the localized message "invalid_date".
 * - Otherwise, it calculates hours, minutes, and seconds from the timestamp in milliseconds and formats them with leading zeros.
 *
 * @param timestamp number | string - Time in milliseconds since 1970, or a string in "HH:MM:SS" format.
 * @returns string - A formatted time string "HH:MM:SS" or "invalid_date" if the input is invalid.
 */
export const getTimeFromTimestamp = (timestamp) => {
    if (typeof timestamp === 'string' && /^\d{2}:\d{2}:\d{2}$/.test(timestamp)) {
        return timestamp;
    }

    if (timestamp == null || isNaN(timestamp))   return i18n.t('invalid_date');

    const ms = typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp;

    const hours = String(Math.floor(ms / 3600000)).padStart(2, '0');
    const minutes = String(Math.floor((ms % 3600000) / 60000)).padStart(2, '0');
    const seconds = String(Math.floor((ms % 60000) / 1000)).padStart(2, '0');

    return `${hours}:${minutes}:${seconds}`;
};

/**
 * Converts a timestamp (number or string) into a formatted date string.
 * - If the timestamp is invalid, it returns the localized message "invalid_date".
 * - The output format includes year, month, and day according to the locale (`lCode`).
 *
 * @param timestamp number | string - The timestamp in milliseconds since 1970 (can be a number or a string).
 * @returns string - A localized date string or "invalid_date" if the input is not valid.
 */
export const getDateFromTimestamp = (timestamp) => {
    const date = new Date(
        typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp
    );
    if (isNaN(date.getTime())) {
        return i18n.t('invalid_date');
    }
    return date.toLocaleDateString(lCode, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
};


/**
 * Converts a timestamp (number or string) into a formatted date and time string.
 * - If the timestamp is invalid, it returns a localized "invalid_date" message.
 * - The output format includes year, month, day, hour, and minute (24-hour format).
 *
 * @param timestamp number | string - The timestamp in milliseconds since 1970 (can be a string or a number).
 * @returns string - A localized formatted date/time string or "invalid_date" if the input is invalid.
 */
export const getDateTimeFromTimestamp = (timestamp) => {
    const date = new Date(
        typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp
    );
    if (isNaN(date.getTime())) {
        return i18n.t('invalid_date');
    }
    return date.toLocaleString(lCode, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
};

/**
 * Converts a timestamp into a valid Date object.
 * - Returns null if the timestamp is null, undefined, or not a valid number.
 * - If the timestamp has 10 digits, it is assumed to be in seconds and converted to milliseconds.
 * - Returns a valid Date object or null if the date cannot be created.
 *
 * @param timestamp number | string - Timestamp in seconds or milliseconds.
 * @returns Date | null - A valid Date object or null if the timestamp is invalid.
 */
export const toValidDate = (timestamp) => {
    if (!timestamp && timestamp !== 0) return null;
    const num = Number(timestamp);
    if (isNaN(num)) return null;
    const ms = num.toString().length === 10 ? num * 1000 : num;
    const date = new Date(ms);
    return isNaN(date.getTime()) ? null : date;
};

/**
 * Converts a given Date (or the current date if null) to a timestamp in milliseconds.
 * The time is normalized to the start of the day (00:00:00).
 *
 * @param item Date object (optional). If null, the current date is used.
 * @returns number Timestamp in milliseconds at the start of the day.
 */
export const getDateLong = (item) => {
    const date = item != null ? item : new Date();
    const momentDate = moment(date).startOf('day');
    return momentDate.valueOf();
};

/**
 * Converts a date-like value to a Unix timestamp in milliseconds,
 * preserving the exact time (hours, minutes, seconds, milliseconds).
 *
 * Accepts various input types:
 * - `null` or `undefined`: returns `null`.
 * - `number`: assumed to be a valid timestamp (in ms) and returned as-is.
 * - `string` or `Date`: parsed into a moment object; if invalid, returns `null`.
 *
 * @param {string | Date | number | null | undefined} item - The input date value.
 * @returns {number | null} The Unix timestamp in milliseconds (UTC-based) or `null` if invalid.
 */
export const getDateLongAsIs = (item) => {
    if (item == null) return null;
    if (typeof item === 'number') return item;
    const momentDate = moment(item);
    if (!momentDate.isValid()) return null;
    return momentDate.valueOf();
};


/**
 * Returns a date formatted according to the given format.
 * Useful for saving dates in the database.
 *
 * @param {Date|string|number} date - The date to format.
 * @param {string} [format='YYYY-MM-DD'] - The desired output format.
 * @returns {string} The formatted date string.
 */
export const getDateWithFormat = (date, format = 'YYYY-MM-DD') => {
    if (!date) return '';
    return moment(date).format(format);
};

/**
 * Converts a total number of minutes into a human-readable string of hours and minutes.
 *
 * For example:
 * - 125 minutes → "2 h 5 min"
 * - 60 minutes → "1 h"
 * - 45 minutes → "45 min"
 * - null or 0 → "0 min"
 *
 * @param {number | null | undefined} totalMinutes - The total number of minutes to convert.
 * @returns {string} A formatted string representing the hours and minutes.
 *
 * @example
 * formatMinutesToHoursMinutes(125); // "2 h 5 min"
 * formatMinutesToHoursMinutes(60);  // "1 h"
 * formatMinutesToHoursMinutes(45);  // "45 min"
 * formatMinutesToHoursMinutes(0);   // "0 min"
 * formatMinutesToHoursMinutes(null); // "0 min"
 */
export function formatMinutesToHoursMinutes(totalMinutes) {
    if (!totalMinutes) return null;

    const minutes = Math.floor(totalMinutes);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    const hoursPart = hours > 0 ? `${hours} h` : '';
    const minutesPart = remainingMinutes > 0 ? `${remainingMinutes} min` : '';

    return [hoursPart, minutesPart].filter(Boolean).join(' ') || '0 min';
}


/**
 * Converts a given date to a timestamp in milliseconds,
 * adjusting the time to the start of the day (00:00:00).
 *
 * @param {string | Date} date - The date to be converted. Can be a Date object or a date string.
 * @returns {number | null} - The timestamp in milliseconds representing the start of the given day,
 *                             or null if the input is invalid.
 */
export const getStartOfDayTimestamp = (date) => {
    if (!date) return null;

    const eventDateObj = new Date(date); // Ensure we have a Date object
    eventDateObj.setHours(0, 0, 0, 0);   // Set time to 00:00:00.000
    return eventDateObj.getTime();       // Return timestamp in milliseconds
};

/**
 * Returns the timestamp representing the end of the given day.
 *
 * The function normalizes the provided date to the last millisecond
 * of the day (23:59:59.999) and returns its epoch timestamp.
 *
 * @param {Date | string | number} date - The input date to normalize.
 * Can be a Date object, ISO string, or timestamp.
 *
 * @returns {number} The timestamp (in milliseconds) at the end of the day.
 */
export const getEndOfDayTimestamp = (date) => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d.getTime();
};

/**
 * Function to validate if end time is after start time.
 */
export const isEndTimeAfterStartTime = (startTime, endTime, format='HH:mm:ss') => {
    const start = moment(startTime, format, true);
    const end = moment(endTime, format, true);

    if (!start.isValid() || !end.isValid()) return false; // safety check

    return end.isAfter(start);
};

/**
 * Formats a time string (e.g. "11:11:11") to "HH:mm".
 * @param {string} timeString - Time in format "HH:mm:ss"
 * @param format
 * @returns {string} Time in "HH:mm" format
 */
export const getTimeFormatFromTime = (timeString, formatIn="HH:mm:ss", formatOut="HH:mm" ) => {
    return moment(timeString, formatIn).format(formatOut);
};

/**
 * Converts a time string in the format "HH:mm" to a Date object.
 *
 * @function getToTime
 * @param {string} time - The time string in "HH:mm" format.
 * @returns {Date|null} Returns a Date object representing the given time (with a fixed date of January 1, 1970),
 *                      or null if the input is invalid or empty.
 *
 * @example
 * getToTime("14:30"); // Returns Date object for 2:30 PM (Jan 1, 1970)
 * getToTime(""); // Returns null
 */
export const getToTime = (time) => {
    if (!time) return null;

    const [hours, minutes] = time.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;
    return new Date(1970, 0, 1, hours, minutes, 0, 0);
}

// Function to get the appropriate file icon based on file extension
export const getImageByExt = (ext) => {
    // Finding the file type object that matches the provided extension
    const fileType = Object.values(FILES_ICON).find((file) => {
        return file.extension.toUpperCase() === ext.toUpperCase();
    });
    // Returning the associated icon if found, or the default PDF icon if not found
    return fileType?.icon || pdf_image;
};

export const getIconByExtOrFile = (fileSelected) => {
    if(!fileSelected)
        return IconFileEmpty;

    if(typeof fileSelected === "string"){
        return getImageByExt(fileSelected?.split('.').pop());
    }else{
        return getFileIcon(fileSelected);
    }
}

export const getToken = () => {
    const token = sessionStorage.getItem('bearerToken');
    const bytes = cryptoJs.AES.decrypt(token, securekey);
    return bytes.toString(cryptoJs.enc.Utf8);
};

export const isValidLengthDocumentId = (value) => {
    return (value.length >= 6 && value.length <= 10) || (value.length >= 16 && value.length <= 20);
};

/**
 * Validates whether a value length is within the allowed range.
 *
 * @param {string} value - The value to validate.
 * @param {number} min - The minimum allowed length.
 * @param {number} max - The maximum allowed length.
 * @returns {boolean} Returns true if the value length is between min and max (inclusive), otherwise false.
 */
export const validateLengthRange = (value, min, max) => {
    return (value.length >= min && value.length <= max);
};

export const isEmailFormat = (email) => {
    return EMAIL_REGEX.test(email);
};
export const isPhoneFormat = (phone) => {
    let isValid;
    if (phone.includes('+')) {
        isValid = PHONE_REGEX.test(phone);
    } else {
        isValid = PHONE_REGEX.test('+' + phone);
    }
    return isValid;
};

export const isIpFormat = (ip) => {
    return IP_REGEX.test(ip);
};

export const getFileSizeFromBase64 = (base64String) => {
    // Remove data URL part if present (like "data:image/png;base64,...")
    const base64 = base64String.split(',')[1] || base64String;

    // Calculate the size in bytes
    const padding = (base64.match(/=+$/) || [''])[0].length;
    return (base64.length * 3) / 4 - padding;
};


/**
 * Converts a byte value into megabytes (MB).
 *
 * @param {number} bytes - The size in bytes to be converted.
 * @param {number} [decimals=2] - Number of decimal places to round the result.
 * @returns {number} The converted value in megabytes. Returns 0 if input is invalid.
 */
export const getBytesToMB = (bytes, decimals = 2) => {
    if (!bytes || isNaN(bytes)) return 0;
    const mb = bytes / (1024 * 1024);
    return parseFloat(mb.toFixed(decimals));
};


//Concat name, last name and second last name
export const userDataTransform = (user) => {
    if (!user || !user.name || !user.lastName) {
        return '-';
    }
    const secondLastName = user.secondLastName != null ? user.secondLastName : ' ';
    return `${user.name} ${secondLastName} ${user.lastName}`;
};

/**
 * Transforms a list of visitor data objects to a standardized format.
 *
 * - Adds a `label` field using the visitor's full name if available,
 *   otherwise concatenates first name, last name, and second last name.
 * - Maps email and phone number fields to `email` and `phoneNumber`.
 * - Preserves all existing properties of each item.
 *
 * @param {Array<Object>} tras - The array of visitor data to transform.
 * @returns {Array<Object>|undefined} The transformed array with normalized visitor fields, or undefined if input is falsy.
 */
export const userDataTransformVisitor = (tras) => {
    return tras?.map((item) => {
            return {
                ...item,
                label: item?.visitorFullName ? item?.visitorFullName : `${item?.visitorName || ''} ${item?.visitorLastName || ''} ${item?.visitorSecondLastName || ''}`.trim(),
                email: item?.visitorEmail,
                phoneNumber: item?.visitorPhoneNumber
            };
        });
    };


export const buildFullNameLabel = (item) => {
    if (item?.fullName && item.fullName.trim() !== '') {
        return item.fullName.replace(/\s+/g, ' ').trim();
    }

    if (item?.label && item.label.trim() !== '') {
        return item.label.replace(/\s+/g, ' ').trim();
    }

    const fullName = [
        item?.name || '',
        item?.lastName || item?.firstLastName || '',
        item?.secondLastName || '',
    ];

    return fullName
        .filter(Boolean)
        .map(part => part.trim())
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
};

export const getFullName = (firstName, lastName, secondLastName) => {
    return [firstName, lastName, secondLastName]
        .filter(Boolean)
        .join(' ');
};

export const getModelSimpleSearch = (model=TABLES.USER, body={}, size = 20) => {
    return  {
        pagination: {
            page: 0,
            size: size,
        },
        ...body,
        model:model
    }
}

export const userListToLabelEmailPhoneValue = (tras, key = 'userId', showSubSection, Icon) => {
    return tras?.map((item) => ({
        ...item,
        id: item[key],
        label: buildFullNameLabel(item),
        Icon: Icon,
        email: item?.email || '',
        phone: item?.phoneNumber || '',
        userId: item[key] || item?.id,
        subSection: () => {
            if (showSubSection)
                return (
                    <>
                        <div className={"col-6 pl-0 pr-2"}>
                            <div className={"d-flex align-items-center"}>
                                <div className={'mr-8px'}><EmailSubsectionChip /></div>
                                <div className={"flex-grow-1 minw-0"}>
                                    <CustomTooltip
                                        text={item?.email}
                                        classes={`fs-12px text-ellipsis-1-row`}
                                        showIfTruncated={true}
                                    />
                                </div>
                            </div>
                        </div>
                        { item?.phoneNumber &&
                            <div className={"col-6 pr-2"}>
                                <div className={"d-flex align-items-center"}>
                                    <div className={'mr-8px'}><PhoneSubsectionChip/></div>
                                    <div className={"flex-grow-1 minw-0"}>
                                        <CustomTooltip
                                            text={item?.phoneNumber}
                                            classes={`fs-12px text-ellipsis-1-row`}
                                            showIfTruncated={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        }
                    </>
                );
        },
    }));
};



/**
 * Transforms a list of equipment items into a structure commonly used by select or chip-based UI components.
 * Each item becomes an object including a label, an ID key, a folio, an icon, and an optional subsection renderer.
 *
 * @function equipmentListToLabel
 *
 * @param {Array<Object>} tras - Original equipment list.
 * @param {string} [key='id'] - Object key used as the identifier for each mapped item.
 * @param {boolean} [showSubSection=false] - If `true`, the returned object includes a subsection JSX renderer.
 *
 * @param saved
 * @returns {Array<Object>} Mapped list with label, id, folio, icon, and optionally subsection renderer.
 */
export const equipmentListToLabel = (tras, key = 'id', showSubSection) => {
    return tras?.map(item => ({
        label : buildFullNameLabel(item),
        id : item[key],
        code: item?.code,
        saved: item?.saved,
        Icon: (props) => (
            <IconChipCircleTools
                {...props}
                className="icon-color-general-950"
            />
        ),
        subSection: () => {
            if (showSubSection)
                return (
                    <div className={"col-12 p-0 d-flex justify-content-start align-items-center gap-2"}>
                        <div>
                            <NumericFieldChip className='icon-color-shades-600'/>
                        </div>
                        <div className={"minw-0"}>
                            <CustomTooltip
                                text={item?.code}
                                classes={"text-ellipsis-1-row fs-12px"}
                                showIfTruncated={true}
                            />
                        </div>
                    </div>
            );
        },
    }));
}

export const deviceListToLabel = (list, showSubSection=true) => {
    if(!list) return [];
    return list?.map((item) => {
        const deviceType = getDeviceTypeById(item?.deviceTypeId);

        return {
            id: item.id,
            label: item?.name || '',
            Icon: deviceType?.icon || null,
            subSection: () => {
                if (showSubSection)
                    return (
                        <>
                            <div className={"col-12 p-0"}>
                                <p className={"fs-12px"}>{deviceType?.trans ? t(deviceType?.trans): ""}</p>
                            </div>
                        </>
                    );
            },
        };
    });
};

/**
 * Transforms a list of department objects into a structure compatible with label-based UI components.
 * @param {Array<Object>} tras - List of department objects to transform.
 * @param {string} [key='id'] - Property name used as the unique identifier.
 * @param {boolean} showSubSection - Determines whether the employee count subsection should be rendered.
 * @param {React.ComponentType} Icon - Optional icon component associated with the item.
 *
 * @returns {Array<Object>} A mapped array of objects formatted for label-based UI rendering.
 */
export const departmentListToLabel = (tras, key = 'id', showSubSection, Icon) => {
    return tras?.map((item) => ({
        id: item[key],
        label: item.name,
        Icon: Icon,
        subSection: ({ divider= true}) => {
            if (showSubSection)
                return (
                    <div className={`col-12 pl-0 pr-2 ${divider ? 'border-top-shades-300' : ''}`}>
                        <div className={"d-flex align-items-end"}>
                            <div className={"pr-2"}><CompletePersonChip/></div>
                            <div className={"fs-14px"}>{item.totalEmployees}</div>
                        </div>
                    </div>
                );
        },
    }));
};

// Function to transform vehicle data for displaying vehicle details
export const vehicleDataTransform = (tras) => {
    return tras?.map((item) => {
        return {
            label: buildVehicleFullName(item),
            id: item?.id,
        };
    });
};

// Function to transform vehicle data using model and color
export const vehicleListToLabel = (
    tras,
    key,
    showSubSection,
    Icon
) => {
    return tras?.map((item) => {
        const model = item?.model || '';
        const color = item?.color || '';

        return {
            id: item?.id,
            label: buildVehicleFullName(item),
            model,
            color,
            Icon,
             subSection: showSubSection
        ? () => (
            <div className="d-flex w-100 justify-content-between text-muted">
              <div className="d-flex align-items-center gap-2">
                <ModelIcon className='w-h-16px'/>
                <span>{model}</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <IconColorPalette className='w-h-16px' />
                <span>{color}</span>
              </div>
            </div>
          )
        : null
        };
    });
};




/**
 * Builds the full display name for a vehicle using its brand, sub-brand, and plate number.
 *
 * Format:
 * {Brand} | {SubBrand}, Plates: {Plate}
 *
 * @param {Object} item - Vehicle information object.
 * @param {string} [item.brand] - Vehicle brand.
 * @param {string} [item.subBrand] - Vehicle sub-brand.
 * @param {string} [item.plate] - Vehicle plate number.
 *
 * @returns {string} Formatted full vehicle name string.
 */
export const buildVehicleFullName = (item) => {
    return `${(item?.brand || '') +
                ' | ' +
                (item?.subBrand || '') +
                ', ' +
                t('plates') +
                ': ' +
                (item?.plate || '')}`;
};

export const filterByLabelEmailPhone = createFilterOptions({
    limit: 500,
    matchFrom: 'any',
    stringify: (option) => `${option.label || ''} ${option.email || ''} ${option.phone || ''}`
});

/**
 * Filters a list of options based on a provided input value.
 * The comparison is case-insensitive and checks if the option's name includes the input text.
 *
 * @function filterByName
 * @param {Array<Object>} options - The array of option objects to filter. Each object should have a `name` property.
 * @param {string} inputValue - The text to filter the options by.
 * @returns {Array<Object>} Returns a filtered array of options whose `name` includes the input value.
 *
 * @example
 * const options = [{ name: 'Apple' }, { name: 'Banana' }, { name: 'Grape' }];
 * filterByName(options, 'ap'); // Returns [{ name: 'Apple' }, { name: 'Grape' }]
 *
 * @example
 * filterByName(options, ''); // Returns all options (no filtering applied)
 */
export const filterByName = (options, inputValue) => {
    if (!inputValue) return options;

    const lowerInput = inputValue.toLowerCase();

    return options.filter(option =>
        (option.name || '').toLowerCase().includes(lowerInput)
    );
};

/**
 * Filters a list of options by matching the input value against
 * the `label`, `email`, or `phoneNumber` fields.
 *
 * - The comparison is case-insensitive.
 * - If no input value is provided, all options are returned.
 *
 * @param {Array<Object>} options - The list of options to filter. Each option may contain `label`, `email`, and `phoneNumber` fields.
 * @param {string} inputValue - The user input to match against the option fields.
 * @returns {Array<Object>} A filtered array of options that include the input value in any of the searchable fields.
 */
export const filterByNameEmailPhoneNumber = (options, inputValue) => {
    if (!inputValue) return options;

    const lowerInput = inputValue.toLowerCase();

    return options.filter(option =>
        `${option.label || ''}${option?.email || ''}${option?.phoneNumber || ''}`.toLowerCase().includes(lowerInput)
    );
};

export const truncateFloat = (num, decimals = 4) => {
    const factor = 10 ** decimals;
    return Math.trunc(num * factor) / factor;
};

export const getMaxWith = (selector, assign) => {
    const divs = document.querySelectorAll(selector);
    let maxWidth = 0;

    divs.forEach((div) => {
        const width = div.offsetWidth;
        if (width > maxWidth) {
            maxWidth = width;
        }
    });
    if (maxWidth !== 0) {
        const assignTo = document.querySelectorAll(assign);
        assignTo.forEach((div) => {
            div.style.width = `${maxWidth}px`;
        });
    }
};

export const errorMsg = (code, params = {}, components={}) => {
    const type = warnCodeTranslation[code] ? 'warning' : (errorCodeTranslation[code] ? 'error' : 'error');
    const messageKey = warnCodeTranslation[code] || errorCodeTranslation[code] || 'something_went_wrong_2';

    toast[type]( <Trans
        i18nKey={messageKey}
        values={params}
        components={components}
        tOptions={{ interpolation: { escapeValue: false } }}
    />);
}

/**
 * Retrieves and formats a value from a given data object based on the field ID and field type.
 * If the value is missing or undefined, returns a "not yet" translation.
 *
 * @param {string|number} fieldId - The unique identifier of the field to retrieve.
 * @param {string} fieldType - The type of the field (e.g., DATE, DATE_TIME, TIME).
 * @param {Object} data - The object containing field data, where each entry may include `fieldId` and `value`.
 * @returns {string} - The formatted value as a string, or the "not yet" translation if not available.
 *
 * @example
 * getValueFromData('field1', 'DATE', formData);
 * // Returns "12/03/2025" if value is present and formatted, or "Not yet" otherwise.
 */
export const getValueFromData = (fieldId, fieldType, data) => {
    if (!fieldId || !data) return i18n.t('not_yet');
    const foundEntry = Object.values(data).find(entry => entry?.fieldId === fieldId);
    if (!foundEntry || foundEntry.value === undefined || foundEntry.value === null)
        return i18n.t('not_yet');
    const value = foundEntry.value;

    switch ((fieldType || '').toUpperCase()) {
        case FIELD_TYPE.DATE.name:
            return getDateFromTimestamp(value);
        case FIELD_TYPE.DATE_TIME.name:
            return getDateTimeFromTimestamp(value);
        case FIELD_TYPE.TIME.name:
            return getTimeFromTimestamp(value);
        default:
            if (typeof value === 'object') return JSON.stringify(value);
            return String(value);
    }
};

/**
 * Convert units to readable formats (KB, MB, GB, TB)
 *
 * @param {number} bytes - The size in bytes.
 * @param {number} decimals - Number of decimals to display.
 * @returns {string} Formatted size (e.g., "1.23 MB").
 */

export const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';

    const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = parseFloat((bytes / Math.pow(k, i)).toFixed(decimals));

    return `${size} ${units[i]}`;
}


/* Opens or downloads a file by dynamically creating a temporary <a> element
 and simulating a click event to trigger the browser's default behavior.*/
export const downloadActionFile = (path) => {
    const link = document.createElement("a");
    link.href = path;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    link.remove();
};

/* Opens or downloads a file by dynamically creating a temporary <a> element
 and simulating a click event to trigger the browser's default behavior.*/
export const downloadLocalFile = (file) => {
    const url = URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", file.name)
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

/**
 * Download iCalendar (.ics) file content.
 * @Author: Cecilia López Martínez
 *
 * @param data
 */
export const downloadICalendarFile = (data, name) => {
    const blob = new Blob([data], { type: 'text/calendar;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', name+'.ics');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

/**
 * Checks if the provided value is a valid number.
 *
 * @param {any} number - The value to validate.
 * @returns {boolean} True if the value is a number and not NaN, otherwise false.
 */
export const isNumber = (number) => {
    return typeof number === "number" && !isNaN(number);
}

/**
 * Formats a given number into a currency-like string (U.S. format).
 * If the value is not a number, it defaults to 0.
 *
 * @param {number} number - The number to format.
 * @returns {string} A formatted string with 2 decimal places (e.g., "1,234.56").
 */
export const currencyFormat = (number) => {
    number = !isNumber(number) ? 0 : number
    return number.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

export const EMPTY_VALUES = {
    DEFAULT  : "--",
    DEFAULT_NUMBER: "0.0"
}

/**
 * Calculates the elapsed time from a starting date until the current moment
 * and returns it as a formatted string.
 *
 * The calculation takes into account:
 * - Differences in years, months, days, hours, and minutes
 * - Months with different numbers of days
 * - Adjustments when a unit results in a negative value
 *
 * If the elapsed time is less than 1 minute, it returns the text defined
 * by the translation key `just_now`.
 *
 * The result is built dynamically, showing only the units
 * greater than zero in the following order:
 * years → months → days → hours → minutes
 *
 * @param {string | number | Date} startAt
 * The starting date from which the elapsed time will be calculated.
 * Can be a timestamp, a date string, or a Date instance.
 *
 * @returns {string}
 * A string representing the elapsed time (e.g., "2 years 3 months 5 days",
 * "13 days 18 hours", "just_now").
 */
export function calculateTime(startAt) {
    const start = new Date(startAt);
    const end = new Date();

    if (end - start < 60000) return t("just_now");

    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let days = end.getDate() - start.getDate();
    let hours = end.getHours() - start.getHours();
    let minutes = end.getMinutes() - start.getMinutes();

    if (minutes < 0) {
        minutes += 60;
        hours--;
    }

    if (hours < 0) {
        hours += 24;
        days--;
    }

    if (days < 0) {
        const previousMonth = new Date(end.getFullYear(), end.getMonth(), 0);
        days += previousMonth.getDate();
        months--;
    }

    if (months < 0) {
        months += 12;
        years--;
    }

    let result = [];

    if (years > 0) result.push(`${years} ${t("years")}`);
    if (months > 0) result.push(`${months} ${t("months_label")}`);
    if (days > 0) result.push(`${days} ${t("days")}`);
    if (hours > 0) result.push(`${hours} ${t("hours")}`);
    if (minutes > 0 && result.length === 0)
        result.push(`${minutes} ${t("minutes")}`);

    return result.join(" ");
}


/**
 * Calculates the difference in minutes between two timestamps (in milliseconds).
 * @param {number|null|undefined} entryDate - Entry timestamp in milliseconds
 * @param {number|null|undefined} exitDate - Exit timestamp in milliseconds
 * @returns {number|null} Difference in minutes, or null if the input is invalid
 * (e.g., non-numeric values, exit before entry, or non-positive timestamps)
 */
export const calculateTotalTimeInMinutes = (entryDate, exitDate) => {
    if (
        typeof entryDate !== 'number' ||
        typeof exitDate !== 'number' ||
        exitDate <= entryDate ||
        exitDate <= 0 ||
        entryDate <= 0
    ) {
        return null;
    }
    return Math.floor((exitDate - entryDate) / 60000);
};


// 🔹 Checks if a given date is before today's date (ignores time).
// Returns true if 'date' occurs before the start of the current day.
export const isBeforeToday = (date) => new Date(date).getTime() < new Date().setHours(0, 0, 0, 0);

// 🔹 Compares two dates and checks if the first date is the same or later than the second.
// Returns true if 'date1' is equal to or after 'date2'.
export const isAfterOrEqual = (date1, date2) => new Date(date1).getTime() >= new Date(date2).getTime();


/**
 * Retrieves the user's current geographic location using the browser's Geolocation API.
 * If geolocation is available and permission is granted, the latitude and longitude are
 * updated using the provided setter functions and stored in sessionStorage.
 * If geolocation is not supported, default coordinates are used instead.
 *
 * @function getGeolocation
 * @param {Object} params - Object containing the setter functions for latitude and longitude.
 * @param {Function} params.setLatitude - Function to update the latitude state.
 * @param {Function} params.setLongitude - Function to update the longitude state.
 * @returns {void}
 *
 *
 * // If geolocation is available, it stores values in sessionStorage as:
 * // sessionStorage.latitude and sessionStorage.longitude
 */
export const getGeolocation = ({ setLatitude, setLongitude }) => {
    if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                setLatitude(lat);
                setLongitude(lng);
                sessionStorage.setItem('latitude', lat);
                sessionStorage.setItem('longitude', lng);
            }
        );
    }else{
        setLatitude(DEFAULT_LATITUDE);
        setLongitude(DEFAULT_LONGITUDE);
    }
}

/**
 * Calculates and dynamically updates the height of a given element
 * based on its position in the viewport and the available window height.
 * It also listens for window resize events to recalculate the height automatically.
 *
 * @function calculateHeight
 * @param {Object} params - Function parameters.
 * @param {React.RefObject<HTMLElement>} params.elementRef - Reference to the DOM element whose height will be adjusted.
 * @param {Function} [params.setHeight] - Optional callback to update the height state externally.
 * @param {number} [params.bottom=0] - Optional offset (in pixels) from the bottom of the viewport.
 * @returns {Function} A cleanup function that removes the resize event listener.
 *
 * @example
 * useEffect(() => {
 *   const cleanup = calculateHeight({ elementRef, setHeight, bottom: 50 });
 *   return cleanup;
 * }, []);
 */
export const calculateHeight = ({ elementRef, setHeight, bottom=0 }) => {
    const updateHeight = () => {
        if (elementRef?.current) {
            const rect = elementRef.current.getBoundingClientRect();
            const availableHeight = window.innerHeight - (rect.top + bottom);
            elementRef.current.style.height = `${availableHeight}px`;
            if(setHeight) setHeight(`${availableHeight}px`);
        }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
}

/**
 * Retrieves a human-readable address from latitude and longitude coordinates
 * using the OpenStreetMap Nominatim reverse geocoding API.
 * Ensures that only one fetch request is active at a time to avoid duplicates.
 *
 * @async
 * @function tryGetAddress
 * @param {number} lat - Latitude coordinate.
 * @param {number} lng - Longitude coordinate.
 * @param {Function} dispatch - Redux dispatch function (reserved for potential side effects or updates).
 * @param {Function} setDirection - Callback function to set the formatted address.
 * @returns {Promise<void>} Resolves when the address has been fetched or an error has occurred.
 *
 * @example
 * await tryGetAddress(20.5888, -100.3899, dispatch, setDirection);
 */
export const tryGetAddress = async (lat, lng, dispatch, setDirection) => {
    if (!lat || !lng) return;
    if (currentFetch) return;

    currentFetch = true;
    try {
        const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
            {
                headers: {
                    "User-Agent": "IEntry/1.0 (yunuenpozos@gmail.com)",
                    "Accept-Language": "es"
                }
            }
        );

        if (!response.ok){
            setDirection("");
            return;
        }

        const data = await response.json();

        if (data?.display_name) {
            setDirection(data.display_name);
        } else {
            setDirection("");
        }
    } catch (err) {
        setDirection("");
    } finally {
        currentFetch = false;
    }
};

/**
 * Retrieves a specific value from an object model by matching either the `id` or `name` property.
 *
 * This function searches through the given model (object) to find an entry where
 * the `id` or `name` matches the provided `value`. If found, it returns the value
 * of the specified `keyValue` property from that entry. If no match is found, it
 * returns a default empty value.
 */
export function getValueByKey(value, keyValue, model) {
    const entry = Object.entries(model).find(
        e => {
            return (e[1]?.id == value ||  e[1]?.name == value)
        }
    );
    return entry ? entry[1][keyValue] : EMPTY_VALUES.DEFAULT;
}


/**
 * Sanitizes a string by:
 * - Truncating at the first dot (removing extension and duplicates like ".pdf.pdf")
 * - Replacing hyphens and underscores with spaces
 * - Normalizing whitespace
 * @param {string} str - Input string
 * @returns {string} Sanitized string
 */
export function sanitizeFileName(str) {
    if (typeof str !== 'string') return '';
    const baseName = str.split('.')[0];
    let result = baseName.replace(/[-_]/g, ' ');
    result = result.replace(/\s+/g, ' ').trim();
    return result;
}

/**
 * Sanitizes a string value by trimming whitespace and normalizing empty values to null.
 *
 * <p>This utility ensures that input values are safely converted into a consistent format:
 * <ul>
 *   <li>Returns {@code null} if the input is {@code null} or {@code undefined}</li>
 *   <li>Converts the value to a string</li>
 *   <li>Trims leading and trailing whitespace</li>
 *   <li>Returns {@code null} if the resulting string is empty</li>
 * </ul>
 *
 * @param {*} value The input value to sanitize
 * @returns {string|null} The sanitized string or null if empty/invalid
 */
export const sanitizeString = (value) => {
    if (value === null || value === undefined) return null;

    const trimmed = String(value).trim();

    return trimmed === '' ? null : trimmed;
};

export const isNotEmpty = (value) => sanitizeString(value) !== null;

export const isPositiveNumber = (value) => {
    const number = Number(sanitizeString(value));
    return !Number.isNaN(number) && number > 0;
};

/**
 * Resets all checkboxes with class `checkbox` and type `checkbox`
 * within the specified container to unchecked (`checked = false`).
 *
 * The `container` parameter supports multiple input types:
 * - `undefined` or `null`: Uses `document` (searches globally).
 * - `string`: Treated as a CSS selector (e.g., `#id`, `.class`, `[name="..."]`, `div[data-role="panel"]`).
 *   → If no element matches, the function exits silently (no error).
 * - `HTMLElement`: Direct DOM element (e.g., `ref.current`, `event.target`, `document.body`).
 * - Any other type (e.g., number, boolean, object): Ignored silently.
 *
 * ⚠️ This is a DOM-based utility. Prefer state-driven resets in React
 * for controlled components.
 *
 * @param {string | HTMLElement | null | undefined} [container] - Scoping target.
 * @returns {void}
 *
 * @example
 * // Reset checkboxes globally (entire document)
 * resetAllCheckboxes();
 *
 * @example
 * // Reset inside an element with ID "settings-form"
 * resetAllCheckboxes('#settings-form');
 *
 * @example
 * // Reset inside elements with class "filter-group"
 * // (only the first match is used — `querySelector`, not `querySelectorAll`)
 * resetAllCheckboxes('.filter-group');
 *
 * @example
 * // Reset inside an element with name="sidebar" (e.g., <form name="sidebar">)
 * resetAllCheckboxes('[name="sidebar"]');
 *
 * @example
 * // Reset inside a React ref (assuming ref points to a DOM element)
 * const containerRef = useRef(null);
 * // ...
 * resetAllCheckboxes(containerRef.current);
 *
 * @example
 * // Reset inside document body
 * resetAllCheckboxes(document.body);
 */
export const resetAllCheckboxes = (container) => {
    let scope = document;

    if (typeof container === 'string') {
        const el = document.querySelector(container);
        if (!el) return;
        scope = el;
    } else if (container instanceof HTMLElement) {
        scope = container;
    } else if (container != null) {
        // Invalid type → ignore silently
        return;
    }

    const checkboxes = scope.querySelectorAll('input[type="checkbox"].checkbox');
    checkboxes.forEach((el) => {
        if (el instanceof HTMLInputElement) {
            el.checked = false;
        }
    });
};

/**
 * Hook que calcula y actualiza en tiempo real la diferencia entre una fecha y ahora.
 * @param {Date | string | number | null} date - Fecha de inicio
 * @returns {string} Formato: "HH : mm : ss"
 */
export const useRealTimeDuration = (date) => {
    const [durationStr, setDurationStr] = useState('00 : 00 : 00');
    useEffect(() => {
        if (!date) {
            setDurationStr('00 : 00 : 00');
            return;
        }

        let parsed;
        if (typeof date === 'number' && date.toString().length === 13) {
            parsed = moment(date);
        } else if (typeof date === 'string' && /^\d{13}$/.test(date)) {
            parsed = moment(Number(date));
        } else {
            parsed = moment(date);
        }

        if (!parsed.isValid()) {
            setDurationStr('— : — : —');
            return;
        }

        const tick = () => {
            const diffDuration = moment.duration(moment().diff(parsed));
            const h = String(Math.floor(diffDuration.asHours())).padStart(2, '0');
            const m = String(diffDuration.minutes()).padStart(2, '0');
            const s = String(diffDuration.seconds()).padStart(2, '0');
            setDurationStr(`${h} : ${m} : ${s}`);
        };

        tick();
        const intervalId = setInterval(tick, 1000);

        return () => clearInterval(intervalId);
    }, [date]);

    return durationStr;
};


/**
 * Retrieves the list of days for the current month.
 * Generates an array containing all days of the current month, using the day number as both identifier and name.
 *
 * @returns {Array<{id: number, name: string}>}
 */
export const getDaysOfCurrentMonth = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();

    const daysInMonth = new Date(year, month + 1, 0).getDate();

    return Array.from({ length: daysInMonth }, (_, index) => ({
        id: index + 1,
        name: String(index + 1),
    }));
};

/**
 * Converts a time string in HH:mm:ss format into its total value in seconds.
 * If the value is null or empty, it returns null.
 *
 * @param {string} time Time chain in format: HH:mm:ss.
 * @returns {number | null} Total time in seconds or null if the value is invalid.
 */
export const timeToSeconds = (time) => {
    if (!time) return null;
    const [hh = 0, mm = 0, ss = 0] = time.split(':').map(Number);
    return hh * 3600 + mm * 60 + ss;
};

/**
 * Sync i18next language from backend language code.
 *
 * @param {string | undefined | null} backendLang
 * @returns {string} uiLang
 */
export const syncI18nLanguageFromBackend = (backendLang) => {
    const backendLanguage = backendLang ?? UI_TO_BACK_LANG.en;
    const uiLang = BACK_TO_UI_LANG[backendLanguage] ?? BACK_TO_UI_LANG.EN;

    if (i18next.language !== uiLang) {
        i18next.changeLanguage(uiLang);
    }

    return uiLang;
};
/**
 * Converts a time  Mon Aug 18 2014 04:35:35 GMT-0500 in HH:mm:ss format into its total value in seconds.
 * If the value is null or empty, it returns null.
 *
 * @param {string} date Time chain in format: HH:mm:ss.
 * @returns {number | null} Total time in seconds or null if the value is invalid.
 */
export const getSecondsOfDay = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
};

/**
 * Sends a message to the window to notify a change in the session storage loader state.
 * This is commonly used to communicate loading state changes between different contexts
 *
 * @param {boolean} isLoading Indicates whether the loader should be shown (true) or hidden (false).
 */
export function postSessionStorageLoader(isLoading) {
    window.postMessage(
        {
            type: 'sessionStorageChangeLoader',
            newValueLoading: isLoading,
        },
        '*',
    );
}


/**
 * Validates that a text value meets a minimum length after trimming spaces.
 * @param {string|null|undefined} value Value to validate.
 * @param {number|Object} config Minimum length number or configuration object.
 * @param {number} [config.minLength=6] Minimum length required after trimming spaces.
 * @param {boolean} [config.allowEmpty=true] Whether an empty value is considered valid.
 * @returns {boolean} Validation result.
 */
export const validateMinLengthTrimmed = (value, config = 6) => {
    const rawValue = value ?? "";
    const trimmedValue = rawValue.trim();

    const minLength =
        typeof config === "number" ? config : config.minLength ?? 6;

    const allowEmpty =
        typeof config === "object" ? config.allowEmpty ?? true : true;

    if (rawValue === "") {
        return allowEmpty;
    }

    if (rawValue.length > 0 && trimmedValue.length === 0) {
        return false;
    }

    return trimmedValue.length >= minLength;
};

/**
 * Validates whether a timestamp represents a valid DATE or DATE_TIME value.
 *
 * @param {number|string|null|undefined} ts - Timestamp in milliseconds or numeric string.
 * @returns {boolean} Returns true if the timestamp represents a valid date within the allowed year range.
 */
export const isValidTimestampDateTime = (ts) => {
    if (ts == null || ts === "") return true;
    const value = Number(ts);
    if (!Number.isFinite(value)) return false;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return false;
    const y = d.getFullYear();
    return y >= MIN_YEAR && y <= MAX_YEAR;
};

/**
 * Validates whether a timestamp represents a valid TIME value.
 *
 * @param {number|string|null|undefined} ms - Milliseconds since midnight or numeric string.
 * @returns {boolean} Returns true if the value is within the valid time range of a day.
 */
export const isValidTimestampTime = (ms) => {
    if (ms == null || ms === "") return true;
    const value = Number(ms);
    if (!Number.isFinite(value)) return false;
    return value >= 0 && value < 24 * 60 * 60 * 1000;
};

/**
 * Validates whether a value represents a valid date-like object.
 * @param {*} v - Value to validate.
 * @returns {boolean} Returns true if the value can be interpreted as a valid date or time.
 */
export const isValidDateLike = (v) => {
    if (v == null || v === "") return true;
    if (typeof v?.isValid === "function") return v.isValid();
    if (v instanceof Date) return !Number.isNaN(v.getTime());
    if (typeof v === "number") {
        const d = new Date(v);
        return !Number.isNaN(d.getTime());
    }
    if (typeof v === "string") {
        const value = v.trim();
        if (!value) return true;
        const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)(:([0-5]\d))?$/;
        if (timeRegex.test(value)) return true;
        return !Number.isNaN(Date.parse(value));
    }
    return false;
};

/**
 * Validates that a date value falls within the allowed year range.
 *
 * The function supports multiple input formats including:
 * - Date objects
 * - Numeric timestamps (milliseconds)
 * - Numeric timestamp strings
 * - Date-like objects from libraries such as dayjs or moment
 *
 * @param {*} v - Value representing a date.
 * @returns {boolean} Returns true if the date is valid and its year is between MIN_YEAR and MAX_YEAR.
 */
export const isValidMinMaxYear = (v) => {
    if (v == null || v === "") return true;

    // dayjs / moment
    if (typeof v?.isValid === "function") {
        if (!v.isValid()) return false;
        const y = v.year();
        return y >= MIN_YEAR && y <= MAX_YEAR;
    }

    let d;

    // timestamp (number o string)
    const num = Number(v);
    if (Number.isFinite(num)) {
        d = new Date(num);
    } else if (v instanceof Date) {
        d = v;
    } else {
        d = new Date(v);
    }

    if (Number.isNaN(d.getTime())) return false;

    const y = d.getFullYear();
    return y >= MIN_YEAR && y <= MAX_YEAR;
};

/**
 * Formats a file size from bytes to a human-readable string in KB or MB.
 *
 * If the size is greater than or equal to 1 MB, the value is returned in MB.
 * Otherwise, the value is returned in KB.
 *
 * @param {number} bytes - File size in bytes.
 * @returns {string} Formatted file size string (e.g., "512 KB", "2 MB").
 */

export const formatFileSize = (bytes) => {
    if (!bytes) return '';

    const mb = 1024 * 1024;

    if (bytes >= mb) {
        return `${(bytes / mb).toFixed(0)} MB`;
    }

    return `${(bytes / 1024).toFixed(0)} KB`;
};

/**
 * Determines whether the current window width falls within the "sm" breakpoint range.
 *
 * The "sm" range is defined as a width greater than or equal to 576px
 * and less than 768px.
 *
 * @function isSmWidth
 * @returns {boolean} Returns true if the window width is within the "sm" range; otherwise, false.
 */
export const isSmWidth= () => {
    const width = window.innerWidth;

    // sm (>=576 y <768)
    return width >= 576 && width < 768
}

